/**
 * AdminAssessmentDashboard Component
 * 
 * Comprehensive assessment management dashboard with three tabs:
 * 1. Manage Assessments - CRUD operations for assessments
 * 2. Performance - Analytics and metrics
 * 3. Configuration - Settings and AI generation rules
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardActions,
    Grid,
    Chip,
    IconButton,
    CircularProgress,
    Tabs,
    Tab,
    Alert,
    Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PlayArrow as PlayIcon,
    Settings as SettingsIcon,
    Assessment as AssessmentIcon,
    BarChart as StatsIcon,
    Publish as PublishIcon,
    Unpublished as UnpublishIcon,
    Star as StarIcon,
    Schedule as ScheduleIcon,
    QuestionAnswer as QuestionIcon
} from '@mui/icons-material';
import {
    AdminAssessmentDTO,
    getAssessmentsByTopic,
    deleteAssessment,
    saveAssessment,
    toggleAssessmentStatus
} from '../../services/adminAssessmentService';
import AssessmentEditor from './AssessmentEditor';
import AssessmentPerformance from './AssessmentPerformance';
import AssessmentConfigurationModal from './AssessmentConfigurationModal';

interface AdminAssessmentDashboardProps {
    topicId: number;
    subjectId?: number;
}

/**
 * AdminAssessmentDashboard - Main component for managing assessments
 * 
 * Features:
 * - List all assessments for a topic
 * - Create, edit, delete assessments
 * - Preview assessments as a student
 * - Publish/unpublish assessments
 * - View performance analytics
 * - Configure assessment settings
 */
const AdminAssessmentDashboard: React.FC<AdminAssessmentDashboardProps> = ({ topicId, subjectId }) => {
    const [assessments, setAssessments] = useState<AdminAssessmentDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [error, setError] = useState<string | null>(null);

    // Editor state
    const [isEditing, setIsEditing] = useState(false);
    const [assessmentToEdit, setAssessmentToEdit] = useState<AdminAssessmentDTO | undefined>(undefined);

    // Preview state
    const [previewAssessment, setPreviewAssessment] = useState<AdminAssessmentDTO | null>(null);

    // Config Modal State
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);

    useEffect(() => {
        loadAssessments();
    }, [topicId]);

    /**
     * Load all assessments for the topic
     */
    const loadAssessments = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAssessmentsByTopic(topicId);
            setAssessments(data);
        } catch (err) {
            console.error('Failed to load assessments', err);
            setError('Failed to load assessments. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle assessment deletion
     */
    const handleDelete = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this assessment? This action cannot be undone.')) {
            try {
                await deleteAssessment(id);
                loadAssessments();
            } catch (err) {
                console.error('Failed to delete assessment', err);
                alert('Failed to delete assessment');
            }
        }
    };

    /**
     * Handle create button click
     */
    const handleCreateClick = () => {
        setAssessmentToEdit(undefined);
        setIsEditing(true);
    };

    /**
     * Handle edit button click
     */
    const handleEditClick = (assessment: AdminAssessmentDTO) => {
        setAssessmentToEdit(assessment);
        setIsEditing(true);
    };

    /**
     * Handle save assessment (create or update)
     */
    const handleSaveAssessment = async (assessmentData: Partial<AdminAssessmentDTO>) => {
        try {
            // Avoid sending empty strings for dates (causes backend deserialization errors)
            const sanitized = { ...assessmentData } as Partial<AdminAssessmentDTO>;
            if (sanitized.scheduledDate === '') delete (sanitized as Record<string, unknown>).scheduledDate;
            if (sanitized.dueDate === '') delete (sanitized as Record<string, unknown>).dueDate;
            await saveAssessment(topicId, sanitized);
            setIsEditing(false);
            setAssessmentToEdit(undefined);
            loadAssessments();
        } catch (err: unknown) {
            const ax = err as { response?: { data?: { message?: string } }; message?: string };
            const serverMessage = ax.response?.data?.message;
            console.error('Failed to save assessment', err);
            alert(serverMessage ? `Failed to save assessment: ${serverMessage}` : 'Failed to save assessment');
        }
    };

    /**
     * Handle cancel editing
     */
    const handleCancelEdit = () => {
        setIsEditing(false);
        setAssessmentToEdit(undefined);
    };

    /**
     * Handle publish/unpublish toggle (uses PATCH /api/admin/assessments/{id}/status)
     */
    const handleTogglePublish = async (assessment: AdminAssessmentDTO) => {
        const id = assessment.id;
        if (id == null || id === undefined) {
            alert('Cannot update status: assessment ID is missing.');
            return;
        }
        const newStatus = assessment.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
        try {
            await toggleAssessmentStatus(id, newStatus);
            loadAssessments();
        } catch (err: unknown) {
            const ax = err as { response?: { data?: { message?: string }; status?: number } };
            const serverMessage = ax.response?.data?.message;
            const status = ax.response?.status;
            console.error('Failed to update assessment status', err);
            if (status === 404) {
                alert(serverMessage || 'Assessment not found. It may have been deleted. Refresh the list.');
            } else {
                alert(serverMessage ? `Failed to update status: ${serverMessage}` : 'Failed to update assessment status');
            }
        }
    };

    /**
     * Get status color for chip
     */
    const getStatusColor = (status: string): 'success' | 'warning' | 'default' => {
        switch (status) {
            case 'PUBLISHED':
                return 'success';
            case 'DRAFT':
                return 'warning';
            default:
                return 'default';
        }
    };

    /**
     * Format date for display
     */
    const formatDate = (dateString?: string): string => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Show editor if editing
    if (isEditing) {
        return (
            <AssessmentEditor
                topicId={topicId}
                subjectId={subjectId}
                existingAssessment={assessmentToEdit}
                onSave={handleSaveAssessment}
                onCancel={handleCancelEdit}
            />
        );
    }

    // Show preview if previewing
    if (previewAssessment) {
        return (
            <Box sx={{ height: '80vh' }}>
                <Box mb={2}>
                    <Button variant="outlined" onClick={() => setPreviewAssessment(null)}>
                        ← Back to Assessments
                    </Button>
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>Preview Mode:</strong> This is how students will see this assessment. No data will be saved.
                </Alert>
                <Card>
                    <CardContent>
                        <Typography variant="h5">{previewAssessment.title}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {previewAssessment.description || 'No description provided'}
                        </Typography>
                        <Box mt={2}>
                            <Typography variant="body2">
                                <strong>Duration:</strong> {previewAssessment.durationMinutes || 'Unlimited'} minutes
                            </Typography>
                            <Typography variant="body2">
                                <strong>Passing Score:</strong> {previewAssessment.passingScore}%
                            </Typography>
                            <Typography variant="body2">
                                <strong>Questions:</strong> {previewAssessment.questionCount || 0}
                            </Typography>
                        </Box>
                    </CardContent>
                    <CardActions>
                        <Button variant="contained" disabled>
                            Start Assessment (Preview Only)
                        </Button>
                    </CardActions>
                </Card>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                    <AssessmentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Topic Assessments
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateClick}>
                    Create Assessment
                </Button>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                    <Tab label="Manage Assessments" icon={<AssessmentIcon />} iconPosition="start" />
                    <Tab label="Performance" icon={<StatsIcon />} iconPosition="start" />
                    <Tab label="Configuration" icon={<SettingsIcon />} iconPosition="start" />
                </Tabs>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Tab 0: Manage Assessments */}
            {tabValue === 0 && (
                <Grid container spacing={3}>
                    {loading && (
                        <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                        </Grid>
                    )}

                    {!loading && assessments.length === 0 && (
                        <Grid item xs={12}>
                            <Alert severity="info">
                                No assessments found for this topic. Create one to get started.
                            </Alert>
                        </Grid>
                    )}

                    {assessments.map((assessment) => (
                        <Grid item xs={12} md={6} lg={4} key={assessment.id}>
                            <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        {assessment.title}
                                    </Typography>
                                    
                                    {assessment.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {assessment.description.length > 100
                                                ? assessment.description.substring(0, 100) + '...'
                                                : assessment.description}
                                        </Typography>
                                    )}

                                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                        <Chip
                                            icon={<QuestionIcon />}
                                            label={`${assessment.questionCount || 0} Questions`}
                                            size="small"
                                            variant="outlined"
                                        />
                                        {assessment.durationMinutes && (
                                            <Chip
                                                icon={<ScheduleIcon />}
                                                label={`${assessment.durationMinutes} min`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                        <Chip
                                            label={assessment.status}
                                            size="small"
                                            color={getStatusColor(assessment.status)}
                                        />
                                    </Box>

                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Pass Score: {assessment.passingScore}% | Max Attempts: {assessment.maxAttempts}
                                    </Typography>
                                    
                                    {assessment.scheduledDate && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Scheduled: {formatDate(assessment.scheduledDate)}
                                        </Typography>
                                    )}
                                </CardContent>

                                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                                    <Box>
                                        <Tooltip title="Try as Student">
                                            <Button
                                                size="small"
                                                startIcon={<PlayIcon />}
                                                onClick={() => setPreviewAssessment(assessment)}
                                            >
                                                Preview
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title={assessment.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}>
                                            <Button
                                                size="small"
                                                startIcon={assessment.status === 'PUBLISHED' ? <UnpublishIcon /> : <PublishIcon />}
                                                color={assessment.status === 'PUBLISHED' ? 'warning' : 'success'}
                                                onClick={() => handleTogglePublish(assessment)}
                                            >
                                                {assessment.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                    <Box>
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => handleEditClick(assessment)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(assessment.id!)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Tab 1: Performance */}
            {tabValue === 1 && (
                <AssessmentPerformance topicId={topicId} assessments={assessments} />
            )}

            {/* Tab 2: Configuration */}
            {tabValue === 2 && (
                <Box py={4}>
                    <Typography variant="h6" gutterBottom>
                        Assessment Configuration
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Configure default settings for assessments in this topic.
                        These settings override subject-level configuration and can be
                        used for AI-powered question generation.
                    </Typography>

                    <Button
                        variant="contained"
                        startIcon={<SettingsIcon />}
                        onClick={() => setIsConfigModalOpen(true)}
                    >
                        Manage Configuration
                    </Button>

                    <AssessmentConfigurationModal
                        open={isConfigModalOpen}
                        onClose={() => setIsConfigModalOpen(false)}
                        level="TOPIC"
                        entityId={topicId}
                        subjectId={subjectId}
                    />
                </Box>
            )}
        </Box>
    );
};

export default AdminAssessmentDashboard;
